import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../lib/assets";
import { TreeDeciduous, Compass, Pyramid, Amphora, Globe } from "lucide-react";

const PRINCIPLES = [
  {
    icon: TreeDeciduous,
    title: "Stewardship",
    line: "We are long-term stewards of capital and legacy.",
    text: "We regard the assets entrusted to us as a responsibility carried carefully across generations — protected, nurtured, and passed on intact and enhanced. Stewardship is the quiet discipline of caring for what is not ours alone.",
  },
  {
    icon: Compass,
    title: "Discipline",
    line: "We invest with discipline, patience, and conviction.",
    text: "We act only when our understanding is deep and our conviction genuine. We are equally willing to wait, to decline, and to do nothing. Patience is not passivity; it is the deliberate refusal to confuse activity with progress.",
  },
  {
    icon: Pyramid,
    title: "Integrity",
    line: "We operate with integrity, transparency, and humility.",
    text: "We hold ourselves to a quiet standard of honesty in every relationship. We measure success by trust earned rather than attention gained, and we believe that how we conduct ourselves matters as much as what we achieve.",
  },
  {
    icon: Amphora,
    title: "Freedom",
    line: "We create the freedom to live with purpose and intention.",
    text: "Capital, well stewarded, is not an end but a means — the freedom to choose, to contribute, and to live deliberately. We invest so that those who follow may pursue what matters most to them, unconstrained.",
  },
  {
    icon: Globe,
    title: "Legacy",
    line: "We build enduring value for future generations.",
    text: "Our horizon extends beyond our own. We plant what others will harvest and build what others will inherit, mindful that the most meaningful value compounds slowly and is measured not in years, but in generations.",
  },
];

export default function Principles() {
  return (
    <div data-testid="principles-page">
      <PageHero
        testId="principles-hero"
        eyebrow="Philosophy — Principles"
        title={
          <>
            How we
            <br />
            think.
          </>
        }
        intro="Five principles guide every decision we make. They are not strategies, but convictions — the quiet standards by which we steward capital and legacy."
        image={IMAGES.markets}
        alt="Layered coastal sandstone strata at golden hour"
      />

      <section className="bg-aveden-black py-20 lg:py-28">
        <div className="mx-auto max-w-[1100px] px-6 sm:px-10 lg:px-16">
          {PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title}>
                <div
                  data-testid={`principle-detail-${p.title.toLowerCase()}`}
                  className={`grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 lg:gap-14 py-12 lg:py-16 ${
                    i !== 0 ? "border-t border-white/[0.06]" : ""
                  }`}
                >
                  <div className="flex md:flex-col items-center md:items-start gap-6 md:gap-8">
                    <Icon size={44} strokeWidth={0.8} className="text-aveden-gold shrink-0" />
                    <span className="font-serif text-aveden-gold/50 text-3xl">
                      0{i + 1}
                    </span>
                  </div>
                  <div>
                    <h2 className="font-serif text-2xl lg:text-3xl text-aveden-white tracking-[0.14em] uppercase mb-5">
                      {p.title}
                    </h2>
                    <p className="font-serif font-light text-aveden-white/90 text-xl lg:text-2xl leading-snug mb-6">
                      {p.line}
                    </p>
                    <p className="font-sans font-light text-aveden-gray text-base leading-relaxed max-w-2xl">
                      {p.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
